import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, Pencil, Phone, Search, Users, X } from "lucide-react";
import { cn } from "../lib/cn";

interface ContactsScreenProps {
  accessToken: string;
  apiUrl: string;
  onCall: (number: string) => void;
  isActive: boolean;
}

type Contact = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  phone: string;
  email: string | null;
  company: string | null;
  tags: string[];
  notes: string | null;
  doNotCall: boolean;
  lastContactedAt: string | null;
  createdAt: string;
};

// Fixed palette — same 8 colors cycled by tag string hash for visual variety
const TAG_PALETTE = [
  "bg-blue-100 text-blue-700",
  "bg-green-100 text-green-700",
  "bg-purple-100 text-purple-700",
  "bg-orange-100 text-orange-700",
  "bg-pink-100 text-pink-700",
  "bg-teal-100 text-teal-700",
  "bg-amber-100 text-amber-700",
  "bg-red-100 text-red-700",
];

function tagColor(tag: string): string {
  let hash = 0;
  for (let i = 0; i < tag.length; i++) hash = (hash * 31 + tag.charCodeAt(i)) >>> 0;
  return TAG_PALETTE[hash % TAG_PALETTE.length] ?? TAG_PALETTE[0]!;
}

function getDisplayName(c: Contact): string {
  return [c.firstName, c.lastName].filter(Boolean).join(" ").trim() || c.phone;
}

function getInitials(c: Contact): string {
  if (c.firstName || c.lastName) {
    return [(c.firstName ?? "")[0], (c.lastName ?? "")[0]].filter(Boolean).join("").toUpperCase();
  }
  return c.phone.slice(-2);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const INPUT_CLS =
  "w-full rounded-xl border border-border bg-card px-3 py-2.5 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-primary";

export function ContactsScreen({ accessToken, apiUrl, onCall, isActive }: ContactsScreenProps) {
  const [search, setSearch] = useState("");
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Contact | null>(null);

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    company: "",
    notes: "",
    tags: "",
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset to list when user navigates away from this tab
  useEffect(() => {
    if (!isActive) {
      setSelected(null);
      setEditing(false);
    }
  }, [isActive]);

  const fetchContacts = useCallback(
    async (q: string) => {
      setLoading(true);
      setError(null);
      try {
        const url = `${apiUrl}/contacts?limit=50${q ? `&search=${encodeURIComponent(q)}` : ""}`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!res.ok) throw new Error("Failed to load");
        const data = (await res.json()) as { contacts: Contact[] };
        setContacts(data.contacts ?? []);
      } catch {
        setError("Couldn't load contacts.");
      } finally {
        setLoading(false);
      }
    },
    [accessToken, apiUrl],
  );

  useEffect(() => {
    void fetchContacts("");
  }, [fetchContacts]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void fetchContacts(value);
    }, 300);
  };

  const openContact = (contact: Contact) => {
    setSelected(contact);
    setEditing(false);
    setSaveError(null);
  };

  const openEdit = (contact: Contact) => {
    setEditForm({
      firstName: contact.firstName ?? "",
      lastName: contact.lastName ?? "",
      phone: contact.phone,
      email: contact.email ?? "",
      company: contact.company ?? "",
      notes: contact.notes ?? "",
      tags: contact.tags.join(", "),
    });
    setEditing(true);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    setSaveError(null);
    try {
      const tagsArr = editForm.tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);

      const res = await fetch(`${apiUrl}/contacts/${selected.id}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName: editForm.firstName.trim() || null,
          lastName: editForm.lastName.trim() || null,
          phone: editForm.phone.trim() || null,
          email: editForm.email.trim() || null,
          company: editForm.company.trim() || null,
          notes: editForm.notes.trim() || null,
          tags: tagsArr,
        }),
      });

      if (!res.ok) {
        const body = (await res.json()) as { error?: string };
        setSaveError(body.error ?? "Failed to save.");
        return;
      }

      const data = (await res.json()) as { contact: Contact };
      const updated = data.contact;

      setSelected(updated);
      setContacts((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      setEditing(false);
    } catch {
      setSaveError("Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  // ── Edit form ───────────────────────────────────────────────────────────────
  if (selected && editing) {
    return (
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="flex items-center justify-between border-b border-border px-3 py-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setEditing(false)}
              className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
            <h2 className="text-sm font-semibold text-foreground">Edit contact</h2>
          </div>
          <button
            onClick={() => void handleSave()}
            disabled={saving}
            className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-opacity disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {saveError && (
            <p className="rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {saveError}
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                First name
              </label>
              <input
                value={editForm.firstName}
                onChange={(e) => setEditForm((f) => ({ ...f, firstName: e.target.value }))}
                placeholder="First name"
                className={INPUT_CLS}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Last name
              </label>
              <input
                value={editForm.lastName}
                onChange={(e) => setEditForm((f) => ({ ...f, lastName: e.target.value }))}
                placeholder="Last name"
                className={INPUT_CLS}
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Phone</label>
            <input
              value={editForm.phone}
              onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
              placeholder="+234 801 234 5678"
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Email</label>
            <input
              type="email"
              value={editForm.email}
              onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="email@example.com"
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Company</label>
            <input
              value={editForm.company}
              onChange={(e) => setEditForm((f) => ({ ...f, company: e.target.value }))}
              placeholder="Company name"
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Tags <span className="font-normal text-muted-foreground/70">(comma-separated)</span>
            </label>
            <input
              value={editForm.tags}
              onChange={(e) => setEditForm((f) => ({ ...f, tags: e.target.value }))}
              placeholder="sales, vip, follow-up"
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Notes</label>
            <textarea
              value={editForm.notes}
              onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Add a note…"
              rows={4}
              className={cn(INPUT_CLS, "resize-none")}
            />
          </div>
        </div>
      </div>
    );
  }

  // ── Contact detail ──────────────────────────────────────────────────────────
  if (selected) {
    const name = getDisplayName(selected);
    const initials = getInitials(selected);

    return (
      <div className="flex flex-1 flex-col bg-background">
        {/* Back button */}
        <div className="flex items-center px-3 pt-3">
          <button
            onClick={() => setSelected(null)}
            className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        </div>

        {/* Hero */}
        <div className="flex flex-col items-center gap-2 px-6 py-5">
          {/* Initials circle */}
          <div className="mb-1 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-lg font-semibold text-primary-foreground shadow-sm">
            {initials}
          </div>

          {/* Name */}
          <p className="text-2xl font-semibold tracking-tight text-foreground">{name}</p>

          {/* Company */}
          {selected.company && <p className="text-sm text-muted-foreground">{selected.company}</p>}

          {/* Phone */}
          <p className="text-xs text-muted-foreground">{selected.phone}</p>

          {/* DNC badge */}
          {selected.doNotCall && (
            <span className="rounded-full bg-destructive/10 px-2.5 py-0.5 text-xs font-medium text-destructive">
              Do Not Call
            </span>
          )}

          {/* Call + Edit action buttons */}
          <div className="mt-2 flex gap-2">
            <button
              onClick={() => onCall(selected.phone)}
              className="flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-opacity hover:opacity-90"
            >
              <Phone className="h-4 w-4" />
              Call
            </button>
            <button
              onClick={() => openEdit(selected)}
              className="flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-muted"
            >
              <Pencil className="h-4 w-4" />
              Edit
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6">
          <div className="space-y-5">
            {/* Contact info */}
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Contact info
              </p>
              <div className="overflow-hidden rounded-xl border border-border bg-card divide-y divide-border">
                <div className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-sm text-muted-foreground">Phone</span>
                  <span className="text-sm font-medium text-foreground">{selected.phone}</span>
                </div>
                {selected.email && (
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-sm text-muted-foreground">Email</span>
                    <span className="max-w-[180px] truncate text-sm font-medium text-foreground">
                      {selected.email}
                    </span>
                  </div>
                )}
                {selected.company && (
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-sm text-muted-foreground">Company</span>
                    <span className="text-sm font-medium text-foreground">{selected.company}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Tags */}
            {selected.tags.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Tags
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {selected.tags.map((tag) => (
                    <span
                      key={tag}
                      className={cn(
                        "rounded-full px-2.5 py-0.5 text-xs font-medium",
                        tagColor(tag),
                      )}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {selected.notes && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Notes
                </p>
                <div className="rounded-xl border border-border bg-card px-4 py-3">
                  <p className="text-sm text-foreground whitespace-pre-wrap">{selected.notes}</p>
                </div>
              </div>
            )}

            {/* Details */}
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Details
              </p>
              <div className="overflow-hidden rounded-xl border border-border bg-card divide-y divide-border">
                {selected.lastContactedAt && (
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-sm text-muted-foreground">Last contacted</span>
                    <span className="text-sm font-medium text-foreground">
                      {formatDate(selected.lastContactedAt)}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-sm text-muted-foreground">Added</span>
                  <span className="text-sm font-medium text-foreground">
                    {formatDate(selected.createdAt)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── List view ───────────────────────────────────────────────────────────────
  // Sort alphabetically and group by first letter
  const sortedContacts = [...contacts].sort((a, b) =>
    getDisplayName(a).localeCompare(getDisplayName(b)),
  );
  const grouped: { letter: string; contacts: Contact[] }[] = [];
  for (const c of sortedContacts) {
    const letter = (getDisplayName(c)[0] ?? "#").toUpperCase();
    const last = grouped[grouped.length - 1];
    if (last?.letter === letter) {
      last.contacts.push(c);
    } else {
      grouped.push({ letter, contacts: [c] });
    }
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Search bar */}
      <div className="flex shrink-0 items-center gap-2 px-4 py-3">
        <div className="flex flex-1 items-center gap-2 rounded-xl border border-border bg-card px-3 py-2.5">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search contacts…"
            className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
          {search && (
            <button
              onClick={() => {
                setSearch("");
                void fetchContacts("");
              }}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <p className="text-sm text-muted-foreground">Loading…</p>
          </div>
        ) : error ? (
          <div className="px-4 py-6 text-center">
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        ) : contacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16">
            <Users className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              {search ? "No contacts found." : "No contacts yet."}
            </p>
          </div>
        ) : (
          <div className="space-y-4 py-3">
            {grouped.map(({ letter, contacts: letterContacts }) => (
              <div key={letter}>
                <p className="px-4 pb-2 text-xs font-medium text-muted-foreground">{letter}</p>
                <div className="space-y-1.5 px-4">
                  {letterContacts.map((contact) => {
                    const name = getDisplayName(contact);
                    const initials = getInitials(contact);
                    return (
                      <div
                        key={contact.id}
                        onClick={() => openContact(contact)}
                        className="flex cursor-pointer items-center gap-3 rounded-xl bg-card px-4 py-3 shadow-sm transition-shadow hover:shadow-md"
                      >
                        {/* Avatar */}
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                          {initials}
                        </div>

                        {/* Middle */}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-foreground">{name}</p>
                          <p className="truncate text-xs text-muted-foreground">{contact.phone}</p>
                        </div>

                        {/* Call button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onCall(contact.phone);
                          }}
                          className="shrink-0 rounded-full bg-primary/10 p-2 text-primary transition-colors hover:bg-primary/20"
                          aria-label={`Call ${name}`}
                        >
                          <Phone className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
